import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { Layout } from '../../components/common/Layout';
import { DashboardLayout } from '../../components/common/DashboardLayout';
import { ProtectedRoute } from '../../components/common/ProtectedRoute';
import { InternalAdminRoute } from '../../components/common/InternalAdminRoute';
import { AuthProvider } from '../../contexts/AuthContext';
import { RoleProtectedRoute } from '../../components/common/RoleProtectedRoute';
import { PermissionProtectedRoute } from '../../components/common/PermissionProtectedRoute';
import { RoleRestrictedRoute } from '../../components/common/RoleRestrictedRoute';
import { PageTransition } from '../../components/common/PageTransition';

const GoogleAuthCallback = lazy(() => import('./GoogleAuthCallback').then(module => ({ default: module.GoogleAuthCallback })));
const CareersPage = lazy(() => import('./CareersPage').then(module => ({ default: module.CareersPage })));
const Home = lazy(() => import('./Home').then(module => ({ default: module.Home })));
const SignIn = lazy(() => import('./SignIn').then(module => ({ default: module.SignIn })));
const SignUp = lazy(() => import('./SignUp').then(module => ({ default: module.SignUp })));
const Challenges = lazy(() => import('../jobseeker/Challenges').then(module => ({ default: module.Challenges })));
const ChallengeDetails = lazy(() => import('../jobseeker/ChallengeDetails').then(module => ({ default: module.ChallengeDetails })));
const ImmersiveSystemDesign = lazy(() =>
  import('../jobseeker/ImmersiveSystemDesign').then(module => ({ default: module.ImmersiveSystemDesign }))
);
const ProfileRouter = lazy(() => import('./ProfileRouter').then(module => ({ default: module.ProfileRouter })));
const SelectRole = lazy(() => import('./SelectRole').then(module => ({ default: module.SelectRole })));
const UpdatePassword = lazy(() => import('./UpdatePassword').then(module => ({ default: module.UpdatePassword })));
const SignInSSO = lazy(() => import('./SignInSSO').then(module => ({ default: module.SignInSSO })));
const AcceptInvite = lazy(() => import('../employer/AcceptInvite').then(module => ({ default: module.AcceptInvite })));
const AssociateCompany = lazy(() => import('../employer/AssociateCompany').then(module => ({ default: module.AssociateCompany })));
const AdminTools = lazy(() => import('../admin/AdminTools').then(module => ({ default: module.AdminTools })));
const BrowseJobs = lazy(() => import('../jobseeker/BrowseJobs').then(module => ({ default: module.BrowseJobs })));
const PublicCompanyProfile = lazy(() => import('../jobseeker/PublicCompanyProfile').then(module => ({ default: module.PublicCompanyProfile })));
const EmployerProfile = lazy(() => import('../employer/EmployerProfile').then(module => ({ default: module.EmployerProfile })));
const SubscriptionTiersPage = lazy(() => import('../jobseeker/SubscriptionTiersPage').then(module => ({ default: module.SubscriptionTiersPage })));
const WhyTechfolioPage = lazy(() => import('../common/WhyTechfolioPage').then(module => ({ default: module.WhyTechfolioPage })));
const TermsAndConditionsPage = lazy(() => import('./TermsAndConditionsPage').then(module => ({ default: module.TermsAndConditionsPage })));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage').then(module => ({ default: module.PrivacyPolicyPage })));const SearchResults = lazy(() => import('./SearchResults'));
const AnalyticsPage = lazy(() => import('../jobseeker/AnalyticsPage'));
const PublicUserProfile = lazy(() => import('../employer/PublicUserProfile').then(module => ({ default: module.PublicUserProfile })));
const Messages = lazy(() => import('./Messages').then(module => ({ default: module.Messages })));
const PhoneVerification = lazy(() => import('../jobseeker/PhoneVerification').then(module => ({ default: module.PhoneVerification })));
const InterestedCandidates = lazy(() => import('../employer/InterestedCandidates').then(module => ({ default: module.InterestedCandidates })));
const NotFound = lazy(() => import('./NotFound').then(module => ({ default: module.NotFound })));
const Settings = lazy(() => import('./Settings').then(module => ({ default: module.Settings })));
const EmployerAnalyticsPage = lazy(() => import('../employer/EmployerAnalyticsPage'));
const EmployerSubscriptionTiersPage = lazy(() => import('../employer/EmployerSubscriptionTiersPage').then(module => ({ default: module.EmployerSubscriptionTiersPage })));
const EmployerChallenges = lazy(() => import('../employer/EmployerChallenges').then(module => ({ default: module.EmployerChallenges })));
const EmployerChallengeDetails = lazy(() => import('../jobseeker/EmployerChallengeDetails').then(module => ({ default: module.EmployerChallengeDetails })));
const CareerIntelligence = lazy(() => import('../jobseeker/CareerIntelligence').then(module => ({ default: module.CareerIntelligence })));

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    const teddyAscii = `
   _     _   
  (c).-.(c)  
   / ._. \\   
 __\\( Y )/__ 
(_.-/'-'-\\-._)
   ||Techfol||
       .io 
 _.' \`-'\` '._ 
(.-./\`-'\.-.)
 \`-'     \`-' `;

    console.log(`%c${teddyAscii}`, 'color:rgb(255, 255, 255); font-family: monospace; font-size: 10px; line-height: 1.1;');
    console.log('%cYou debug systems. We debug hiring. → https://techfol.io/careers', 'color:rgb(255, 255, 255); font-size: 14px; font-weight: bold;');
  }, []);

  const suspenseFallback = (
    <div className="w-full h-screen flex items-center justify-center bg-black">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );

  return (
  <GoogleReCaptchaProvider
    reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
    scriptProps={{
      async: false,
      defer: false,
      appendTo: 'head',
      nonce: undefined,
    }}
  >
    <AuthProvider>
      <AnimatePresence mode="wait">
        <Suspense fallback={suspenseFallback}>
          <Routes location={location} key={location.pathname}>
            <Route element={<Layout />}>
              <Route path="/signin-sso" element={<PageTransition><SignInSSO /></PageTransition>} />
              <Route path="/" element={<PageTransition><Home /></PageTransition>} />
              <Route path="/auth/google/callback" element={<PageTransition><GoogleAuthCallback /></PageTransition>} />
              <Route path="/signin" element={<PageTransition><SignIn /></PageTransition>} />
              <Route path="/signup" element={<PageTransition><SignUp /></PageTransition>} />
              <Route path="/auth/reset-password" element={<PageTransition><UpdatePassword /></PageTransition>} />
              <Route path="/accept-invite" element={<PageTransition><AcceptInvite /></PageTransition>} />
              <Route path="/company/:slug" element={<PageTransition><PublicCompanyProfile /></PageTransition>} />
              <Route path="/user/:slug" element={<PageTransition><PublicUserProfile /></PageTransition>} />
              <Route path="/why-techfolio" element={<PageTransition><WhyTechfolioPage /></PageTransition>} />
             <Route path="/terms-and-conditions" element={<PageTransition><TermsAndConditionsPage /></PageTransition>} />
              <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicyPage /></PageTransition>} />
              <Route path="/search" element={<PageTransition><SearchResults /></PageTransition>} />
              <Route path="/careers" element={<PageTransition><CareersPage /></PageTransition>} />
            </Route>
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile/*" element={<PageTransition><ProfileRouter /></PageTransition>} />
              <Route path="employer-profile" element={<PageTransition><EmployerProfile /></PageTransition>} />
              <Route path="challenges" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><Challenges /></PageTransition></RoleProtectedRoute>} />
              <Route path="challenges/:id" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><ChallengeDetails /></PageTransition></RoleProtectedRoute>} />
              <Route path="interview" element={<PageTransition><ImmersiveSystemDesign /></PageTransition>} />
<Route path="career-intelligence" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><CareerIntelligence /></PageTransition></RoleProtectedRoute>} />
<Route path="browse-jobs" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><BrowseJobs /></PageTransition></RoleProtectedRoute>} />              <Route path="select-role" element={<PageTransition><SelectRole /></PageTransition>} />
              <Route path="associate-company" element={<PageTransition><AssociateCompany /></PageTransition>} />
              <Route path="subscription-tiers" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><SubscriptionTiersPage /></PageTransition></RoleProtectedRoute>} />
              <Route path="employer-subscription-tiers" element={<RoleProtectedRoute requiredRole="employer"><RoleRestrictedRoute><PageTransition><EmployerSubscriptionTiersPage /></PageTransition></RoleRestrictedRoute></RoleProtectedRoute>} />
              <Route path="settings" element={<PageTransition><Settings /></PageTransition>} />
              <Route path="analytics" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><AnalyticsPage /></PageTransition></RoleProtectedRoute>} />
              <Route path="employer-analytics" element={<RoleProtectedRoute requiredRole="employer"><PermissionProtectedRoute permission="view_analytics"><PageTransition><EmployerAnalyticsPage /></PageTransition></PermissionProtectedRoute></RoleProtectedRoute>} />
              <Route path="employer-challenges" element={<RoleProtectedRoute requiredRole="employer"><PageTransition><EmployerChallenges /></PageTransition></RoleProtectedRoute>} />
              <Route path="challenges/employer/:invitationId" element={<PageTransition><EmployerChallengeDetails /></PageTransition>} />
              <Route path="employer-challenges/:invitationId" element={<RoleProtectedRoute requiredRole="employer"><PageTransition><EmployerChallengeDetails /></PageTransition></RoleProtectedRoute>} />
              <Route path="interested-candidates" element={<RoleProtectedRoute requiredRole="employer"><PageTransition><InterestedCandidates /></PageTransition></RoleProtectedRoute>} />
              <Route path="messages" element={<PageTransition><Messages /></PageTransition>} />
              <Route path="verification" element={<RoleProtectedRoute requiredRole="job_seeker"><PageTransition><PhoneVerification /></PageTransition></RoleProtectedRoute>} />

              <Route path="why-techfolio" element={<PageTransition><WhyTechfolioPage /></PageTransition>} />
            </Route>
            <Route path="/admin" element={<InternalAdminRoute><DashboardLayout /></InternalAdminRoute>}>
              <Route path="tools" element={<AdminTools />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </AuthProvider>
  </GoogleReCaptchaProvider>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

export default App;